"use client"

import { CATEGORY_KEYS, categorize } from "../../blog/blog-list"
import type { BlogPostMeta } from "../../blog/posts"
import { BLOG_LIST_T } from "../../blog/translations"
import type { FullTranslation } from "../../lib/i18n"
import { useAutoleadsLocale } from "../../lib/locale-context"
import { useTr } from "../../lib/use-tr"
import { LocaleLink } from "../locale-link"
import { Reveal } from "../reveal"
import { ArrowRight, Sparkles } from "lucide-react"

const BLOG_T = {
  eyebrow: { FR: "Ressources", EN: "Resources" },
  heading: {
    FR: "Tout ce qu'on apprend en construisant AutoLeads.",
    EN: "Everything we learn while building AutoLeads.",
  },
  subhead: {
    FR: "Guides, comparatifs et retours d'expérience pour prospecter mieux en 2026.",
    EN: "Guides, comparisons and field notes to prospect better in 2026.",
  },
  seeAll: { FR: "Voir tous les articles", EN: "See all articles" },
  readTimeSuffix: { FR: "de lecture", EN: "read" },
} satisfies FullTranslation

// Banner gradients cycle by card position to keep visual variety independent of content.
const BANNER_CLASSES = [
  "bg-[linear-gradient(135deg,oklch(0.88_0.09_50/0.55),oklch(0.92_0.04_50/0.15))]",
  "bg-[linear-gradient(135deg,oklch(0.85_0.08_200/0.55),oklch(0.92_0.03_200/0.15))]",
  "bg-[linear-gradient(135deg,oklch(0.86_0.07_140/0.55),oklch(0.92_0.03_140/0.15))]",
]

export function BlogPreview({ posts }: { posts: BlogPostMeta[] }) {
  const tr = useTr(BLOG_T)
  const trCat = useTr(BLOG_LIST_T)
  const locale = useAutoleadsLocale()
  const dateLocale = locale === "FR" ? "fr-FR" : "en-GB"

  return (
    <section
      className="relative py-16 sm:py-20 md:py-24"
      aria-labelledby="blog-preview-heading"
    >
      <Reveal className="sm:container">
        <div className="mb-12 flex flex-col gap-6 text-left sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <div className="bg-card/60 text-muted-foreground mb-5 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
              <Sparkles className="text-primary-cta size-3.5" aria-hidden />
              <span>{tr("eyebrow")}</span>
            </div>
            <h2
              id="blog-preview-heading"
              className="text-foreground font-display leading-display tracking-dense text-3xl font-semibold sm:text-4xl md:text-5xl"
            >
              {tr("heading")}
            </h2>
            <p className="text-muted-foreground text-md mt-4 max-w-md leading-relaxed sm:text-base">
              {tr("subhead")}
            </p>
          </div>

          <LocaleLink
            href="/blog"
            className="group text-muted-foreground hover:text-foreground text-2sm inline-flex items-center gap-1.5 font-medium transition-colors"
          >
            {tr("seeAll")}
            <ArrowRight
              className="size-3.5 transition-transform group-hover:translate-x-0.5"
              aria-hidden
            />
          </LocaleLink>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {posts.map((post, index) => {
            const content = post.content[locale]
            const category = categorize(post, locale)
            const date = new Date(post.publishedAt).toLocaleDateString(
              dateLocale,
              { month: "long", year: "numeric" },
            )
            const readTime = `${post.readingMinutes} min`
            return (
              <LocaleLink
                key={post.slug}
                href={`/blog/${post.slug}`}
                className="group bg-card hover:border-foreground/20 flex flex-col overflow-hidden rounded-2xl border transition-colors"
              >
                <div
                  className={`relative h-32 ${BANNER_CLASSES[index % BANNER_CLASSES.length]}`}
                >
                  <span className="bg-card/80 text-foreground text-2xs absolute top-4 left-4 inline-flex items-center rounded-full border px-2.5 py-0.5 font-medium backdrop-blur-sm">
                    {trCat(CATEGORY_KEYS[category])}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="text-foreground text-md line-clamp-2 font-semibold tracking-tight">
                    {content.title}
                  </h3>
                  <p className="text-muted-foreground text-2sm mt-2 line-clamp-2 leading-relaxed">
                    {content.description}
                  </p>
                  <div className="text-muted-foreground mt-5 flex items-center justify-between border-t pt-4 text-xs">
                    <span className="capitalize">{date}</span>
                    <span>
                      {readTime} {tr("readTimeSuffix")}
                    </span>
                  </div>
                </div>
              </LocaleLink>
            )
          })}
        </div>
      </Reveal>
    </section>
  )
}
